// Ui.Form is handed its fields as opaque Html, so in Elm nothing says which
// of them a failed submit left invalid. This reads it from the DOM instead:
// every Ui.Form control marks itself aria-invalid, and after a submit the
// first one that is gets the focus. No page passes an id or wires a port.
//
// The errors of a submit can arrive later than the submit, from the server,
// so the form stays armed until an invalid control appears. It stands down as
// soon as the person focuses or types in a field, so it never pulls focus
// away from someone who has moved on.

const INVALID = '[aria-invalid="true"]:not(:disabled)';
const ERROR = '.form-error, [role="alert"]';
const KEEPS_ENTER = '[role="combobox"], [data-field-suggesting]';
const SEARCH = '[role="search"], search';

const rendered = (element) => element.getClientRects().length > 0;

function inView(element) {
  const rect = element.getBoundingClientRect();

  return rect.top >= 0 && rect.bottom <= window.innerHeight && rect.left >= 0 && rect.right <= window.innerWidth;
}

function reveal(element) {
  if (!inView(element)) element.scrollIntoView({ behavior: "smooth", block: "center" });
}

class ElmForm extends HTMLElement {
  connectedCallback() {
    this.armed = false;
    this.addEventListener("submit", this);
    this.addEventListener("focusin", this);
    this.addEventListener("input", this);
    this.addEventListener("keydown", this);
    this.observer = new MutationObserver(() => this.settle());
    this.observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-invalid"],
    });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  handleEvent(event) {
    if (event.type === "keydown") this.keepEnter(event);
    else if (event.type === "submit") this.arm(event);
    else this.armed = false;
  }

  // Enter in a combobox belongs to its list, open or not: choosing an option
  // must never also submit the form around it. Ui.Form.Input.suggesting marks
  // a plain input for as long as its suggestions are showing.
  // A search region inside the form narrows what the form shows; Enter in its
  // inputs is not an answer to the form.
  keepEnter(event) {
    if (event.key !== "Enter") return;
    if (event.target.matches(KEEPS_ENTER) || this.searches(event.target)) event.preventDefault();
  }

  searches(target) {
    const search = target.matches("input") && target.closest(SEARCH);

    return Boolean(search) && search.closest("elm-form") === this;
  }

  arm(event) {
    if (event.target.closest("elm-form") !== this) return;

    this.armed = true;
    // Elm renders what the submit changed on the next frame. A field that
    // was already invalid changes nothing, so the observer alone would miss it.
    requestAnimationFrame(() => requestAnimationFrame(() => this.settle()));
  }

  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest("elm-form") === this && rendered(el));
  }

  settle() {
    if (!this.armed) return;

    const [control] = this.own(INVALID);
    const [error] = this.own(ERROR).filter((el) => el.textContent.trim());

    if (control) {
      this.armed = false;
      control.focus({ preventScroll: true });
      reveal(control);
    } else if (error) {
      this.armed = false;
      reveal(error);
    }
  }
}

if (!customElements.get("elm-form")) {
  customElements.define("elm-form", ElmForm);
}
